import React, { useContext } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Box as Form, Input, Text } from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { contactInstrumentAtom } from 'pages/Contacts/atoms/contactAtoms'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { sendWalletAtom } from './atoms/atoms'
import { SendDetailsCard } from './components/SendDetailsCard'
import { useSendDetailsForm } from './hooks/useSendDetailsForm'

export const SendDetails = () => {
  const { t } = useTranslation()
  const sendWallet = useAtomValue(sendWalletAtom)
  const contactInstrument = useAtomValue(contactInstrumentAtom)
  const [walletAction] = useContext(WalletActionContext)

  const {
    formFields,
    estimate,
    isDisabledReviewPayment,
    isLoadingReviewPayment,
    requestError,
    showTimer,
    timeLeft,
    sameCurrency,
    handleSubmit,
    onSubmit,
    setChangedField,
    setValue,
    setSource,
  } = useSendDetailsForm()

  return (
    <Form
      tag="form"
      flexCol
      gapY={20}
      data-testid="send-details-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <SendDetailsCard />

      {requestError?.show && <RequestErrorCallout requestError={requestError} />}
      <Box flex gapX={12} width="full" justify="between">
        <Box width="full">
          <Input
            testid="send-amount"
            {...formFields.sendAmount}
            disabled={isLoadingReviewPayment}
            onChange={e => {
              setValue('sendAmount', e?.target?.value)
              setChangedField('sendAmount')
              setSource('user')
            }}
            rightSuffixText={sendWallet?.currency?.code}
          />
        </Box>
        {!sameCurrency && (
          <Box width="full">
            <Input
              testid="receive-amount"
              {...formFields.receiveAmount}
              disabled={isLoadingReviewPayment}
              onChange={e => {
                setValue('receiveAmount', e?.target?.value)
                setChangedField('receiveAmount')
                setSource('user')
              }}
              rightSuffixText={contactInstrument?.currency}
            />
          </Box>
        )}
      </Box>
      <Box width="full" data-testid="details-list" paddingY={8}>
        <Box flexCol width="full" gapY={8}>
          {!sameCurrency && (
            <Box flex justify="between" gapX={16}>
              <Text color="subdued">
                {showTimer
                  ? t('wallets.send.exchangeRateUpdate', { seconds: timeLeft })
                  : t('wallets.send.exchangeRate')}
              </Text>
              <Box className="text-end">
                <Text variant="body-label">
                  {estimate && showTimer
                    ? `1 ${estimate?.walletCurrency} = ${estimate?.exchangeRate} ${estimate?.paidCurrency}`
                    : '-'}
                </Text>
              </Box>
            </Box>
          )}
          <Box flex justify="between" gapX={16}>
            <Text color="subdued">{t('wallets.send.processingFee')}</Text>
            <Box className="text-end">
              <Text variant="body-label">
                {estimate ? `${estimate?.feePredictedAmount} ${estimate?.feeCurrency}` : '-'}
              </Text>
            </Box>
          </Box>
          <Box flex justify="between" gapX={16}>
            <Text color="subdued">{t('wallets.send.networkFee')}</Text>
            <Box className="text-end">
              <Text variant="body-label">
                {estimate
                  ? `${estimate?.networkFeePredictedAmount} ${estimate?.networkFeeCurrency}`
                  : '-'}
              </Text>
            </Box>
          </Box>
          <Box flex justify="between" gapX={16}>
            <Text color="subdued">{t('wallets.send.totalSubtracted')}</Text>
            <Box className="text-end">
              <Text variant="body-label">
                {estimate ? `${estimate?.totalWalletAmount} ${estimate?.walletCurrency}` : '-'}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box width="full">
        <Input
          testid="reference"
          {...formFields.reference}
          disabled={isLoadingReviewPayment}
          onChange={e => {
            setValue('reference', e?.target?.value)
            setChangedField('reference')
            setSource('user')
          }}
        />
      </Box>

      <Box flex justify="between">
        <Button testId="back" variant="secondary" onClick={walletAction?.onBack}>
          {t('back')}
        </Button>

        <Button
          testId="review-payment"
          type="submit"
          variant="primary"
          loading={isLoadingReviewPayment}
          disabled={isDisabledReviewPayment}
        >
          {t('wallets.send.reviewPayment')}
        </Button>
      </Box>
    </Form>
  )
}
